import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  CampaignReportResponse,
  ComparisonReportResponse,
  HessReportResponse,
  LinearReportResponse,
  SocialCampaignReportResponse,
  StreamingReportResponse,
  UploadRepairNotice,
} from "../types";
import { getReport } from "../utils/reportManager";
import type { SavedReport } from "../utils/reportManager";

export type UploadResultsData =
  | CampaignReportResponse
  | ComparisonReportResponse
  | LinearReportResponse
  | StreamingReportResponse
  | SocialCampaignReportResponse
  | HessReportResponse;

export type UploadResultsMetadata = SavedReport["metadata"];

const NOTICE_LEVEL_ORDER: Record<UploadRepairNotice["level"], number> = {
  info: 0,
  warning: 1,
  error: 2,
};

export function useUploadResultsReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<UploadResultsData | null>(null);
  const [currentReportId, setCurrentReportId] = useState<string | null>(null);
  const [reportProduct, setReportProduct] = useState<string | null>(null);
  const [reportMetadata, setReportMetadata] = useState<UploadResultsMetadata>(
    null,
  );
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  useEffect(() => {
    const reportId = searchParams.get("id");
    if (!reportId) {
      navigate("/upload");
      return;
    }

    const savedReport = getReport(reportId);
    if (!savedReport) {
      navigate("/upload");
      return;
    }

    setData(savedReport.data);
    setCurrentReportId(reportId);
    setReportProduct(savedReport.metadata?.product ?? null);
    setReportMetadata(savedReport.metadata ?? null);
    window.history.replaceState(null, "", `/upload/results?id=${reportId}`);
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!data?.repair_notices || data.repair_notices.length === 0) {
      setShowNoticeModal(false);
      return;
    }

    const highestLevel = data.repair_notices.reduce<UploadRepairNotice["level"]>(
      (acc, notice) =>
        NOTICE_LEVEL_ORDER[notice.level] > NOTICE_LEVEL_ORDER[acc]
          ? notice.level
          : acc,
      data.repair_notices[0].level,
    );

    setShowNoticeModal(highestLevel !== "info");
  }, [data]);

  return {
    currentReportId,
    data,
    reportMetadata,
    handleNewUpload: () => navigate("/upload"),
    reportProduct,
    setShowNoticeModal,
    showNoticeModal,
  };
}
